import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';

import {
  RouterModule,
  Router,
  NavigationEnd,
  ActivatedRoute,
  Route,
} from '@angular/router';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { HeaderComponent } from './components/header/header.component';
import { filter, map, mergeMap, startWith } from 'rxjs';
import { Subscription } from 'rxjs';
import { UserStateService } from '../services/user-state/user-state.service';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { MenuItem } from 'primeng/api';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { EmailVerificationService } from '../auth/email-verification/service/email-verification.service';

interface BreadcrumbItem {
  label: string;
  link: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-layout',
  imports: [
    RouterModule,
    SidebarComponent,
    HeaderComponent,
    BreadcrumbComponent,
    TranslateModule,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  isExpanded: boolean = true;
  isMobile: boolean = false;
  currentUser: string = '';
  pageTitle: string = '';
  breadcrumbItems: BreadcrumbItem[] = [];
  breadcrumbHome!: MenuItem;

  isDarkTheme: boolean = true;

  private readonly MOBILE_BREAKPOINT: number = 768;
  private routerSubscription: Subscription;
  private langChangeSubscription: Subscription;
  private currentRouteData: Record<string, string> = {};
  private emailVerificationService: EmailVerificationService = inject(EmailVerificationService);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public userStateService: UserStateService,
    private translateService: TranslateService
  ) {
    this.breadcrumbHome = {
      link: '/',
      label: this.translateService.instant('layout.sidebar.home'),
    };

    this.routerSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        map(() => {
          let route: ActivatedRoute = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route: ActivatedRoute) => route.outlet === 'primary'),
        mergeMap((route: ActivatedRoute) => route.data)
      )
      .subscribe((data: Record<string, string>) => {
        this.currentRouteData = data;
        this.updateTranslations();
      });

    // Suscribirse a los cambios de idioma
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(
      (_event: LangChangeEvent) => {
        this.updateBreadcrumbHome();
        this.updateTranslations();
      }
    );
  }

  private updateBreadcrumbHome(): void {
    this.breadcrumbHome = {
      link: '/',
      label: this.translateService.instant('layout.sidebar.home'),
    };
  }

  private updateTranslations(): void {
    const data: Record<string, string> = this.currentRouteData;
    if (data['title']) {
      const translatedTitle: string = this.translateService.instant(data['title']);
      this.updateBreadcrumb(translatedTitle, this.translateService.instant(data['module'])); 
    }
    if (data['module']) {
      const translatedModule: string = this.translateService.instant(data['module']);
      this.pageTitle = translatedModule;
    }
  }

  private updateBreadcrumb(title: string, module?: string) {
    const urlSegments: string[] = this.router.url
      .split('/')
      .filter((segment: string) => segment);

    const actionSegments: Set<string> = new Set(['detail', 'edit', 'create', 'new', 'preview']);
    const isId: (segment: string) => boolean = (segment: string): boolean => /^\d+$/.test(segment);
    const isAction: (segment: string) => boolean = (segment: string): boolean => actionSegments.has(segment);

    const relevantSegments: string[] = urlSegments.filter(
      (segment: string) => !isId(segment) && !isAction(segment)
    );

    const hasActionsOrIds: boolean = urlSegments.some(
      (segment: string) => isId(segment) || isAction(segment)
    );

    const breadcrumbs: BreadcrumbItem[] = [];
    let currentPath: string = '';

    relevantSegments.forEach((segment: string, index: number) => {
      currentPath += `/${segment}`;
      const isLastSegment: boolean = index === relevantSegments.length - 1;
      const hasMultipleSegments: boolean = relevantSegments.length > 1;
      const shouldCreateIntermediate: boolean = isLastSegment && !hasMultipleSegments && hasActionsOrIds;

      if (!shouldCreateIntermediate) {
        const label: string = isLastSegment
          ? title
          : index === 0 && module
            ? module
            : segment.charAt(0).toUpperCase() + segment.slice(1);
        const link: string = isLastSegment ? this.router.url : currentPath;
        
        const disabled: boolean = !isLastSegment && hasMultipleSegments && this.hasOwnViewIsFalse(segment);
        
        breadcrumbs.push({ label, link, disabled });
        return;
      }

      const moduleLabel: string = module || segment.charAt(0).toUpperCase() + segment.slice(1);
      const disabled: boolean = this.hasOwnViewIsFalse(segment);
      breadcrumbs.push({ label: moduleLabel, link: currentPath, disabled });
      breadcrumbs.push({ label: title, link: this.router.url });
    });

    this.breadcrumbItems = breadcrumbs;
  }

  private hasOwnViewIsFalse(segment: string): boolean {
    const layoutRoute: Route | undefined = this.router.config.find(route => route.component === LayoutComponent);
    const moduleRoute: Route | undefined = layoutRoute?.children?.find(route => route.path === segment);

    if (moduleRoute?.data?.['hasOwnView'] === false) return true;

    let currentRoute: ActivatedRoute = this.activatedRoute.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;

      const routeConfig: Route | null = currentRoute.routeConfig;
      if (routeConfig?.path === segment && routeConfig?.data?.['hasOwnView'] === false) 
        return true;
      
      
      if (routeConfig?.path?.includes(segment) && routeConfig?.data?.['hasOwnView'] === false) 
        return true;
      
    }
    
    return false;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkMobileBreakpoint();
  }

  private checkMobileBreakpoint(): void {
    const wasMobile: boolean = this.isMobile;
    this.isMobile = window.innerWidth <= this.MOBILE_BREAKPOINT;
    
    // Auto-close sidebar when entering mobile view
    if (this.isMobile && !wasMobile) this.isExpanded = false;
    // Auto-expand sidebar when leaving mobile view
    if (!this.isMobile && wasMobile) this.isExpanded = true;
  }

  toggleSidebar(): void {
    this.isExpanded = !this.isExpanded;
  }

  closeSidebar(): void {
    this.isExpanded = false;
  }

  toggleTheme() {
    this.isDarkTheme = !this.isDarkTheme;
    const html: HTMLElement = document.documentElement;
    html.classList.toggle('theme-dark', this.isDarkTheme);
    localStorage.setItem('theme', this.isDarkTheme ? 'dark' : 'light');
  }

  ngOnInit(): void {
    const savedTheme: string = localStorage.getItem('theme') || 'dark';
    this.isDarkTheme = savedTheme === 'dark';
    if (this.isDarkTheme) document.documentElement.classList.add('theme-dark');
    
    // Check initial viewport size
    this.checkMobileBreakpoint();

    this.emailVerificationService.promptIfNotVerified();
  }

  ngOnDestroy() {
    if (this.routerSubscription) this.routerSubscription.unsubscribe();
    if (this.langChangeSubscription) this.langChangeSubscription.unsubscribe();
  }
}
